import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { SDivider, SIcon } from "../components";
import { SInputV2 } from "../SInputV2";
export const SWAttributeKeyPicker = ({ value, onChange, suggestions, placeholder = "Key", onBlur })=>{
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef(null);
    const selectedItemRef = useRef(null);
    // Filter suggestions based on search value or current value
    const filteredSuggestions = useMemo(()=>{
        const searchTerm = (searchValue || value || "").toLowerCase();
        if (!searchTerm) return suggestions;
        return suggestions.filter((s)=>s.toLowerCase().includes(searchTerm));
    }, [
        searchValue,
        value,
        suggestions
    ]);
    // Check if we should show "Create" option
    const showCreateOption = useMemo(()=>{
        const currentValue = searchValue || value;
        return currentValue && !suggestions.includes(currentValue);
    }, [
        searchValue,
        value,
        suggestions
    ]);
    // Total items in the list
    const totalItems = filteredSuggestions.length + (showCreateOption ? 1 : 0);
    // Reset selected index when items change
    useEffect(()=>{
        setSelectedIndex(0);
    }, [
        filteredSuggestions,
        showCreateOption
    ]);
    // Scroll selected item into view
    useEffect(()=>{
        if (selectedItemRef.current) {
            selectedItemRef.current.scrollIntoView({
                behavior: "instant",
                block: "nearest",
                inline: "nearest"
            });
        }
    }, [
        selectedIndex
    ]);
    const handleSelect = (selectedValue)=>{
        onChange(selectedValue);
        setSearchValue("");
        setIsOpen(false);
        inputRef.current?.blur();
    };
    const onInputKeyDown = (e)=>{
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedIndex((prev)=>(prev + 1) % totalItems);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedIndex((prev)=>(prev - 1 + totalItems) % totalItems);
        } else if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            setSearchValue("");
            setIsOpen(false);
            inputRef.current?.blur();
        } else if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            if (totalItems === 0) {
                // No options, just close
                setIsOpen(false);
                inputRef.current?.blur();
                return;
            }
            if (showCreateOption && selectedIndex === filteredSuggestions.length) {
                // Create new value
                handleSelect(searchValue || value);
            } else if (filteredSuggestions[selectedIndex]) {
                // Select existing suggestion
                handleSelect(filteredSuggestions[selectedIndex]);
            }
        }
    };
    const handleInputChange = (e)=>{
        const newValue = e.target.value;
        setSearchValue(newValue);
        onChange(newValue);
        if (!isOpen) setIsOpen(true);
    };
    const handleInputFocus = ()=>{
        setIsOpen(true);
        setSearchValue("");
    };
    const handleInputBlur = ()=>{
        // Delay to allow click events to fire
        setTimeout(()=>{
            setIsOpen(false);
            setSearchValue("");
            onBlur?.();
        }, 200);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative flex-1",
        children: [
            /*#__PURE__*/ _jsx(SInputV2, {
                ref: inputRef,
                borderless: false,
                value: searchValue || value,
                onChange: handleInputChange,
                onFocus: handleInputFocus,
                onBlur: handleInputBlur,
                onKeyDown: onInputKeyDown,
                placeholder: placeholder,
                autoComplete: "off"
            }),
            isOpen && totalItems > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "absolute z-50 mt-1 w-full max-h-[400px] overflow-y-auto bg-grayscale-dark rounded-md shadow-xl border border-white/10",
                children: [
                    filteredSuggestions.map((suggestion, index)=>/*#__PURE__*/ _jsxs("div", {
                            children: [
                                index !== 0 && /*#__PURE__*/ _jsx(SDivider, {}),
                                /*#__PURE__*/ _jsx("div", {
                                    ref: (ref)=>{
                                        if (index === selectedIndex) {
                                            selectedItemRef.current = ref;
                                        }
                                    },
                                    className: `grid grid-cols-[1fr] items-center cursor-pointer px-3 py-2 transition-all hover:bg-grayscale-lighter/20 ${selectedIndex === index ? "!bg-grayscale-lighter/20" : ""}`,
                                    onMouseDown: (e)=>{
                                        e.preventDefault();
                                        handleSelect(suggestion);
                                    },
                                    onMouseEnter: ()=>setSelectedIndex(index),
                                    children: /*#__PURE__*/ _jsx("div", {
                                        children: /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm",
                                            children: suggestion
                                        })
                                    })
                                })
                            ]
                        }, `suggestion-${suggestion}`)),
                    showCreateOption && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            filteredSuggestions.length > 0 && /*#__PURE__*/ _jsx(SDivider, {}),
                            /*#__PURE__*/ _jsxs("div", {
                                ref: (ref)=>{
                                    if (selectedIndex === filteredSuggestions.length) {
                                        selectedItemRef.current = ref;
                                    }
                                },
                                className: `grid grid-cols-[auto,1fr] items-center cursor-pointer px-3 py-2 gap-2 transition-all hover:bg-grayscale-lighter/20 ${selectedIndex === filteredSuggestions.length ? "!bg-grayscale-lighter/20" : ""}`,
                                onMouseDown: (e)=>{
                                    e.preventDefault();
                                    handleSelect(searchValue || value);
                                },
                                onMouseEnter: ()=>setSelectedIndex(filteredSuggestions.length),
                                children: [
                                    /*#__PURE__*/ _jsx(SIcon, {
                                        type: "plus",
                                        className: "text-brand-primary",
                                        size: 14
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        children: /*#__PURE__*/ _jsxs("p", {
                                            className: "text-sm",
                                            children: [
                                                'Create "',
                                                searchValue || value,
                                                '"'
                                            ]
                                        })
                                    })
                                ]
                            })
                        ]
                    }, "create-option")
                ]
            })
        ]
    });
};
